/**
 * The order the categories run in, everywhere the site lists them: the hub
 * tab rail, the home "Pick your vibe" strip, the category chips and a city's
 * grouped overview. Same as the app (owner, 2026-10-06): activities,
 * restaurants, spas, stays, then the rest.
 *
 * A page scoped to one city orders by how many bookable places each
 * top-level category has there, most first, and this ranking breaks ties.
 */
export const CATEGORY_RANK = [
  "activities",
  "restaurants",
  "spas",
  "stays",
  "boats",
  "excursions",
  "pools",
  "trips",
  "workshops",
] as const;

const RANK = new Map<string, number>(CATEGORY_RANK.map((slug, i) => [slug, i]));

/** True for a category the rail and the chips offer. */
export function isRankedCategory(slug: string | null | undefined): boolean {
  return !!slug && RANK.has(slug);
}

/**
 * Sorts categories into the ranking. With `counts` (places per top-level
 * category in the page's city), the most stocked come first and the ranking
 * breaks ties. Slugs outside the ranking keep their incoming order, after the
 * ranked ones.
 */
export function rankCategories<T extends { slug?: string | null }>(
  rows: readonly T[],
  counts?: ReadonlyMap<string, number> | null,
): T[] {
  const rank = (slug: string) => RANK.get(slug) ?? CATEGORY_RANK.length;
  return rows
    .map((row, i) => ({ row, i, slug: row.slug ?? "" }))
    .sort((a, b) => {
      if (counts) {
        const d = (counts.get(b.slug) ?? 0) - (counts.get(a.slug) ?? 0);
        if (d !== 0) return d;
      }
      return rank(a.slug) - rank(b.slug) || a.i - b.i;
    })
    .map(({ row }) => row);
}

/**
 * Bookable places per top-level category, counted from a city's listing.
 * A subcategory (quad, hammam) counts toward its parent, found through
 * `parentOf` and walked up to the root.
 */
export function countByRootCategory(
  items: readonly { category?: { slug?: string | null } | null }[],
  parentOf: (slug: string) => string | null | undefined,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const it of items) {
    let slug = it.category?.slug;
    if (!slug) continue;
    for (let hop = 0; hop < 8; hop += 1) {
      const up = parentOf(slug);
      if (!up || up === slug) break;
      slug = up;
    }
    counts.set(slug, (counts.get(slug) ?? 0) + 1);
  }
  return counts;
}
